import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { DomainMapping, HAProxyBackend } from '../../types';

interface DomainMappingFormProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (mapping: Omit<DomainMapping, 'id' | 'backendName' | 'active' | 'sslEnabled'>, backendName: string) => void;
  mapping?: DomainMapping;
  backends: HAProxyBackend[];
}

const DomainMappingForm: React.FC<DomainMappingFormProps> = ({
  isOpen,
  onCancel,
  onSave,
  mapping,
  backends
}) => {
  const [domain, setDomain] = useState('');
  const [backendId, setBackendId] = useState('');

  // Reset form when opened or when editing a different mapping
  useEffect(() => {
    if (isOpen) {
      if (mapping) {
        setDomain(mapping.domain);
        setBackendId(mapping.backendId);
      } else {
        setDomain('');
        setBackendId(backends.length > 0 ? backends[0].id : '');
      }
    }
  }, [isOpen, mapping, backends]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Get the backend name for the selected backendId
    const selectedBackend = backends.find(b => b.id === backendId);
    const backendName = selectedBackend ? selectedBackend.name : '';
    
    onSave({
      domain,
      backendId,
      pattern: '',
      priority: mapping?.priority ?? 100
    }, backendName);
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-10 overflow-y-auto"
        onClose={onCancel}
      >
        <div className="min-h-screen px-4 text-center">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black opacity-50" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-zinc-900 shadow-xl rounded-lg">
              <Dialog.Title
                as="h3"
                className="text-lg font-medium leading-6 text-zinc-100 flex items-center"
              >
                <GlobeAltIcon className="h-5 w-5 mr-2 text-indigo-400" />
                {mapping ? 'Edit Domain Mapping' : 'Add Domain Mapping'}
              </Dialog.Title>
              
              <form onSubmit={handleSubmit} className="mt-4">
                <div className="mb-4">
                  <label htmlFor="domain" className="block text-sm font-medium text-zinc-400 mb-1">
                    Domain
                  </label>
                  <input
                    type="text"
                    id="domain"
                    placeholder="example.com"
                    required
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    className="w-full rounded-md bg-zinc-800 border border-zinc-700 px-4 py-2 text-zinc-200 focus:outline-none focus:border-indigo-500"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="backendId" className="block text-sm font-medium text-zinc-400 mb-1">
                    Backend
                  </label>
                  <select
                    id="backendId"
                    required
                    value={backendId}
                    onChange={(e) => setBackendId(e.target.value)}
                    className="w-full rounded-md bg-zinc-800 border border-zinc-700 px-4 py-2 text-zinc-200 focus:outline-none focus:border-indigo-500"
                  >
                    {backends.length === 0 ? (
                      <option value="" disabled>No backends available</option>
                    ) : (
                      backends.map((backend) => (
                        <option key={backend.id} value={backend.id}>
                          {backend.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 rounded-md text-sm font-medium text-zinc-300 bg-zinc-700 hover:bg-zinc-600 focus:outline-none"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none"
                    disabled={backends.length === 0}
                  >
                    {mapping ? 'Update' : 'Add'} Mapping
                  </button>
                </div>
              </form>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DomainMappingForm; 